// src/pages/InventoryLogsPage.jsx
import React, { useState, useEffect } from 'react';
import { History, Search, Download, Calendar } from 'lucide-react';
import { inventoryApi } from '../api';
import { exportToCSV } from '../utils/csvExport';

// change_type 中文对照——覆盖这次新加的5种，也覆盖批次系统上线前遗留在历史数据里
// 的老值（sale/transfer_in/transfer_out/adjust/return/delete），不然老记录还是显示英文
const CHANGE_TYPE_LABELS = {
  restock:          '进货',
  loss:             '损耗/盘亏',
  found:            '盘盈',
  return_restock:   '可上架退货',
  return_writeoff:  '报废退货',
  // 以下是批次系统上线前的历史遗留值
  sale:             '销售扣减',
  return:           '订单退回',
  adjust:           '库存修正（旧）',
  transfer_in:      '划拨入库',
  transfer_out:     '划拨出库',
  delete:           '删除商品',
};

const CHANGE_TYPE_COLOR = {
  restock:          'bg-green-100 text-green-700',
  loss:             'bg-red-100 text-red-700',
  found:            'bg-blue-100 text-blue-700',
  return_restock:   'bg-teal-100 text-teal-700',
  return_writeoff:  'bg-gray-200 text-gray-700',
  sale:             'bg-orange-100 text-orange-700',
  return:           'bg-purple-100 text-purple-700',
  adjust:           'bg-gray-100 text-gray-600',
  transfer_in:      'bg-cyan-100 text-cyan-700',
  transfer_out:     'bg-amber-100 text-amber-700',
  delete:           'bg-red-50 text-red-500',
};

const InventoryLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productKeyword, setProductKeyword] = useState('');
  const [changeType, setChangeType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadLogs();
  }, [changeType, startDate, endDate]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (changeType) params.change_type = changeType;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await inventoryApi.getLogs(params);
      if (res.success) setLogs(res.data || []);
    } catch (err) {
      console.error('加载库存变动记录失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!productKeyword) return true;
    const kw = productKeyword.toLowerCase();
    return log.product_name?.toLowerCase().includes(kw) || log.barcode?.includes(kw);
  });

  const handleExport = () => {
    exportToCSV(
      filteredLogs.map(log => ({
        ...log,
        change_type_label: CHANGE_TYPE_LABELS[log.change_type] || log.change_type,
        created_at_fmt: new Date(log.created_at).toLocaleString('zh-CN'),
      })),
      [
        { key: 'created_at_fmt', label: '时间' },
        { key: 'product_name', label: '商品名称' },
        { key: 'barcode', label: '条码' },
        { key: 'change_type_label', label: '变动类型' },
        { key: 'quantity', label: '变动数量' },
        { key: 'before_stock', label: '变动前库存' },
        { key: 'after_stock', label: '变动后库存' },
        { key: 'operator_name', label: '操作人' },
        { key: 'notes', label: '备注' },
      ],
      '库存变动记录'
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">库存变动记录</h2>
          <p className="text-xs text-gray-600 mt-1">进货、损耗、销售等所有库存变动的历史流水</p>
        </div>
        <button onClick={handleExport} disabled={filteredLogs.length === 0}
          className="flex items-center gap-1.5 border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
          <Download className="w-4 h-4" />导出
        </button>
      </div>

      {/* 筛选栏 */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder="搜索商品名称/条码"
            value={productKeyword} onChange={e => setProductKeyword(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={changeType} onChange={e => setChangeType(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
          <option value="">全部类型</option>
          {Object.entries(CHANGE_TYPE_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-gray-400" />
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="px-2 py-2 border rounded-lg text-sm" />
          <span className="text-gray-400">至</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="px-2 py-2 border rounded-lg text-sm" />
        </div>
      </div>

      {/* 记录表格 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <History className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">暂无库存变动记录</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-gray-500 text-left">
                <th className="px-4 py-3">时间</th>
                <th className="px-4 py-3">商品</th>
                <th className="px-4 py-3">类型</th>
                <th className="px-4 py-3 text-right">变动数量</th>
                <th className="px-4 py-3 text-right">变动前</th>
                <th className="px-4 py-3 text-right">变动后</th>
                <th className="px-4 py-3">操作人</th>
                <th className="px-4 py-3">备注</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => (
                <tr key={log.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{log.product_name || '（商品已删除）'}</p>
                    <p className="text-xs text-gray-400">{log.barcode}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CHANGE_TYPE_COLOR[log.change_type] || 'bg-gray-100 text-gray-600'}`}>
                      {CHANGE_TYPE_LABELS[log.change_type] || log.change_type}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${log.quantity > 0 ? 'text-green-600' : log.quantity < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {log.quantity > 0 ? '+' : ''}{log.quantity}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-500">{log.before_stock}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{log.after_stock}</td>
                  <td className="px-4 py-3 text-gray-600">{log.operator_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate" title={log.notes}>{log.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InventoryLogsPage;